import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Toolbar,
  alpha,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  PowerOff as PowerOffIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  FileCopy as CloneIcon,
  Terminal as TerminalIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

// ── 정렬 헬퍼 ──────────────────────────────────────────────────────────────────
function getComparator(order, orderBy) {
  return (a, b) => {
    let aVal, bVal;
    switch (orderBy) {
      case 'name':      aVal = a.name || '';        bVal = b.name || '';        break;
      case 'state':     aVal = a.state ?? 99;       bVal = b.state ?? 99;       break;
      case 'ip':
        aVal = (a.network_interfaces?.[0]?.actual_ip_address ||
                a.network_interfaces?.[0]?.ip_address || '');
        bVal = (b.network_interfaces?.[0]?.actual_ip_address ||
                b.network_interfaces?.[0]?.ip_address || '');
        break;
      case 'network':
        aVal = a.network_interfaces?.[0]?.network_profile || '';
        bVal = b.network_interfaces?.[0]?.network_profile || '';
        break;
      case 'autostart': aVal = a.autostart ? 1 : 0; bVal = b.autostart ? 1 : 0; break;
      case 'persistent':aVal = a.persistent ? 1 : 0;bVal = b.persistent ? 1 : 0;break;
      default:          aVal = a.name || '';        bVal = b.name || '';
    }
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  };
}

// ── 상태 칩 ────────────────────────────────────────────────────────────────────
function StatusChip({ state }) {
  const map = {
    1: { color: 'success', label: '실행 중' },
    3: { color: 'warning', label: '일시 중지됨' },
    5: { color: 'error',   label: '크래시됨' },
  };
  const { color = 'default', label = '종료됨' } = map[state] || {};
  return <Chip size="small" color={color} label={label} />;
}

// ── 선택 상태 툴바 ─────────────────────────────────────────────────────────────
function SelectionToolbar({ count, onStart, onStop, onDelete, loading }) {
  if (count === 0) return null;
  return (
    <Toolbar
      sx={{
        pl: 2, pr: 1, mb: 1, borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      <Typography sx={{ flex: 1 }} color="primary" variant="subtitle1" fontWeight="bold">
        {count}개 선택됨
      </Typography>
      <Tooltip title="선택 VM 시작">
        <span>
          <Button
            size="small" startIcon={<PlayIcon />} color="success" variant="outlined"
            onClick={onStart} disabled={loading} sx={{ mr: 1 }}
          >시작</Button>
        </span>
      </Tooltip>
      <Tooltip title="선택 VM 종료 (ACPI)">
        <span>
          <Button
            size="small" startIcon={<StopIcon />} color="warning" variant="outlined"
            onClick={onStop} disabled={loading} sx={{ mr: 1 }}
          >종료</Button>
        </span>
      </Tooltip>
      <Tooltip title="선택 VM 삭제">
        <span>
          <Button
            size="small" startIcon={<DeleteIcon />} color="error" variant="outlined"
            onClick={onDelete} disabled={loading}
          >삭제</Button>
        </span>
      </Tooltip>
    </Toolbar>
  );
}

// ── 정렬 가능한 헤더 셀 ────────────────────────────────────────────────────────
function SortCell({ id, label, orderBy, order, onSort, align }) {
  return (
    <TableCell align={align} sortDirection={orderBy === id ? order : false}>
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : 'asc'}
        onClick={() => onSort(id)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

// ── 메인 컴포넌트 ──────────────────────────────────────────────────────────────
const VMListPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [vms, setVms] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [networkProfiles, setNetworkProfiles] = useState([]);

  // 정렬
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  // 선택
  const [selected, setSelected] = useState(new Set());

  // 강제 종료 다이얼로그
  const [forceStopDialogOpen, setForceStopDialogOpen] = useState(false);
  const [vmToForceStop, setVmToForceStop] = useState(null);

  // 복제 다이얼로그
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [sourceVM, setSourceVM] = useState(null);
  const [cloneConfig, setCloneConfig] = useState({
    new_name: '', include_gpu: false, start_after_clone: true,
    shutdown_if_running: false, network_profile: '',
    clone_port_forwards: false, clone_services: false,
  });

  // 일괄 삭제 다이얼로그
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [removeStorage, setRemoveStorage] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total, errors }

  // ── 데이터 로드 ──────────────────────────────────────────────────────────────
  const fetchVMs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getVMs(showAll);
      setVms(response.data.vms);
      // 삭제된 VM이 선택 목록에 남아있으면 제거
      setSelected(prev => {
        const names = new Set(response.data.vms.map(v => v.name));
        return new Set([...prev].filter(n => names.has(n)));
      });
    } catch {
      enqueueSnackbar('VM 목록을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  useEffect(() => { fetchVMs(); }, [fetchVMs]);

  useEffect(() => {
    apiService.getNetworks()
      .then(r => setNetworkProfiles(r.data.networks || []))
      .catch(() => {});
  }, []);

  // ── 정렬 ─────────────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    setOrder(prev => (orderBy === col && prev === 'asc') ? 'desc' : 'asc');
    setOrderBy(col);
  };

  const sortedVms = [...vms].sort(getComparator(order, orderBy));

  // ── 선택 ─────────────────────────────────────────────────────────────────────
  const isSelected = (name) => selected.has(name);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(new Set(sortedVms.map(v => v.name)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectRow = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const allSelected = sortedVms.length > 0 && selected.size === sortedVms.length;
  const someSelected = selected.size > 0 && !allSelected;

  // ── 단일 VM 액션 ──────────────────────────────────────────────────────────────
  const handleStartVM = async (vmName) => {
    try {
      await apiService.startVM(vmName);
      enqueueSnackbar(`VM ${vmName} 시작됨`, { variant: 'success' });
      fetchVMs();
    } catch (e) {
      enqueueSnackbar(`시작 실패: ${e.response?.data?.detail || e.message}`, { variant: 'error' });
    }
  };

  const handleShutdownVM = async (vmName) => {
    try {
      await apiService.shutdownVM(vmName);
      enqueueSnackbar(`VM ${vmName} 종료 요청됨`, { variant: 'info' });
      fetchVMs();
    } catch (e) {
      enqueueSnackbar(`종료 실패: ${e.response?.data?.detail || e.message}`, { variant: 'error' });
    }
  };

  const handleForceStopClick = (vm) => { setVmToForceStop(vm); setForceStopDialogOpen(true); };
  const handleForceStopConfirm = async () => {
    if (!vmToForceStop) return;
    try {
      await apiService.forceStopVM(vmToForceStop.name);
      enqueueSnackbar(`VM ${vmToForceStop.name} 강제 종료됨`, { variant: 'success' });
      fetchVMs();
    } catch (e) {
      enqueueSnackbar(`강제 종료 실패: ${e.response?.data?.detail || e.message}`, { variant: 'error' });
    } finally {
      setForceStopDialogOpen(false);
      setVmToForceStop(null);
    }
  };

  // ── 복제 ─────────────────────────────────────────────────────────────────────
  const handleOpenCloneDialog = (vm) => {
    setSourceVM(vm);
    setCloneConfig({
      new_name: `${vm.name}_clone`, include_gpu: false, start_after_clone: true,
      shutdown_if_running: false, network_profile: '',
      clone_port_forwards: false, clone_services: false,
    });
    setCloneDialogOpen(true);
  };

  const handleCloneVM = async () => {
    if (!sourceVM) return;
    try {
      setLoading(true);
      const res = await apiService.cloneVM(sourceVM.name, {
        source_vm: sourceVM.name,
        new_name: cloneConfig.new_name,
        include_gpu: cloneConfig.include_gpu,
        start_after_clone: cloneConfig.start_after_clone,
        shutdown_if_running: cloneConfig.shutdown_if_running,
        network_profile: cloneConfig.network_profile || null,
        clone_port_forwards: cloneConfig.clone_port_forwards,
        clone_services: cloneConfig.clone_services,
      });
      enqueueSnackbar(res.data.message, { variant: 'success' });
      setCloneDialogOpen(false);
      fetchVMs();
    } catch (e) {
      enqueueSnackbar(`복제 실패: ${e.response?.data?.detail || e.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ── 일괄 작업 ─────────────────────────────────────────────────────────────────
  const selectedVms = sortedVms.filter(v => selected.has(v.name));

  const handleBulkStart = async () => {
    const targets = selectedVms.filter(v => v.state !== 1);
    if (targets.length === 0) {
      enqueueSnackbar('선택된 VM 중 시작할 수 있는 VM이 없습니다.', { variant: 'info' });
      return;
    }
    setBulkLoading(true);
    setBulkProgress({ done: 0, total: targets.length, errors: [] });
    let errors = [];
    for (let i = 0; i < targets.length; i++) {
      try {
        await apiService.startVM(targets[i].name);
      } catch (e) {
        errors.push(`${targets[i].name}: ${e.response?.data?.detail || e.message}`);
      }
      setBulkProgress({ done: i + 1, total: targets.length, errors });
    }
    setBulkLoading(false);
    if (errors.length === 0) {
      enqueueSnackbar(`${targets.length}개 VM 시작 완료`, { variant: 'success' });
    } else {
      enqueueSnackbar(`${targets.length - errors.length}개 성공, ${errors.length}개 실패`, { variant: 'warning' });
    }
    setBulkProgress(null);
    fetchVMs();
  };

  const handleBulkStop = async () => {
    const targets = selectedVms.filter(v => v.state === 1);
    if (targets.length === 0) {
      enqueueSnackbar('선택된 VM 중 실행 중인 VM이 없습니다.', { variant: 'info' });
      return;
    }
    setBulkLoading(true);
    setBulkProgress({ done: 0, total: targets.length, errors: [] });
    let errors = [];
    for (let i = 0; i < targets.length; i++) {
      try {
        await apiService.shutdownVM(targets[i].name);
      } catch (e) {
        errors.push(`${targets[i].name}: ${e.response?.data?.detail || e.message}`);
      }
      setBulkProgress({ done: i + 1, total: targets.length, errors });
    }
    setBulkLoading(false);
    if (errors.length === 0) {
      enqueueSnackbar(`${targets.length}개 VM 종료 요청 완료`, { variant: 'info' });
    } else {
      enqueueSnackbar(`${targets.length - errors.length}개 성공, ${errors.length}개 실패`, { variant: 'warning' });
    }
    setBulkProgress(null);
    fetchVMs();
  };

  const handleBulkDeleteConfirm = async () => {
    setBulkDeleteOpen(false);
    setBulkLoading(true);
    const targets = [...selected];
    setBulkProgress({ done: 0, total: targets.length, errors: [] });
    let errors = [];
    for (let i = 0; i < targets.length; i++) {
      try {
        await apiService.deleteVM(targets[i], removeStorage);
      } catch (e) {
        errors.push(`${targets[i]}: ${e.response?.data?.detail || e.message}`);
      }
      setBulkProgress({ done: i + 1, total: targets.length, errors });
    }
    setBulkLoading(false);
    setSelected(new Set());
    if (errors.length === 0) {
      enqueueSnackbar(`${targets.length}개 VM 삭제 완료`, { variant: 'success' });
    } else {
      enqueueSnackbar(`${targets.length - errors.length}개 성공, ${errors.length}개 실패`, { variant: 'warning' });
    }
    setBulkProgress(null);
    fetchVMs();
  };

  // ── 렌더 ─────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* 상단 헤더 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">가상머신 목록</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Switch checked={showAll} onChange={e => setShowAll(e.target.checked)} />}
            label="모든 VM 표시"
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchVMs}>
            새로고침
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/vms/create')}>
            새 VM 생성
          </Button>
        </Box>
      </Box>

      {/* 일괄 작업 진행 바 */}
      {bulkProgress && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={(bulkProgress.done / bulkProgress.total) * 100}
          />
          <Typography variant="caption" color="text.secondary">
            {bulkProgress.done} / {bulkProgress.total} 처리 중...
          </Typography>
          {bulkProgress.errors.length > 0 && (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              {bulkProgress.errors.slice(0, 3).join(' | ')}
              {bulkProgress.errors.length > 3 && ` 외 ${bulkProgress.errors.length - 3}건`}
            </Alert>
          )}
        </Box>
      )}

      {/* 선택 툴바 */}
      <SelectionToolbar
        count={selected.size}
        onStart={handleBulkStart}
        onStop={handleBulkStop}
        onDelete={() => setBulkDeleteOpen(true)}
        loading={bulkLoading}
      />

      {/* 테이블 */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={handleSelectAll}
                  disabled={bulkLoading}
                />
              </TableCell>
              <SortCell id="name"       label="이름"     orderBy={orderBy} order={order} onSort={handleSort} />
              <TableCell>ID</TableCell>
              <SortCell id="state"      label="상태"     orderBy={orderBy} order={order} onSort={handleSort} />
              <SortCell id="ip"         label="IP 주소"  orderBy={orderBy} order={order} onSort={handleSort} />
              <SortCell id="network"    label="네트워크" orderBy={orderBy} order={order} onSort={handleSort} />
              <SortCell id="autostart"  label="자동 시작" orderBy={orderBy} order={order} onSort={handleSort} />
              <SortCell id="persistent" label="영구 VM"  orderBy={orderBy} order={order} onSort={handleSort} />
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedVms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    {showAll ? '가상머신이 없습니다.' : '실행 중인 가상머신이 없습니다.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedVms.map((vm) => {
                const sel = isSelected(vm.name);
                const ip = vm.network_interfaces?.[0]?.actual_ip_address ||
                           vm.network_interfaces?.[0]?.ip_address || 'N/A';
                const net = vm.network_interfaces?.[0]?.network_profile || 'default';
                return (
                  <TableRow
                    key={vm.name}
                    hover
                    selected={sel}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSelectRow(vm.name)}
                  >
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={sel}
                        onChange={() => handleSelectRow(vm.name)}
                        disabled={bulkLoading}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={e => { e.stopPropagation(); navigate(`/vms/${vm.name}`); }}
                    >
                      {vm.name}
                    </TableCell>
                    <TableCell>{vm.id > 0 ? vm.id : '-'}</TableCell>
                    <TableCell><StatusChip state={vm.state} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{ip}</TableCell>
                    <TableCell>{net}</TableCell>
                    <TableCell>{vm.autostart ? '예' : '아니오'}</TableCell>
                    <TableCell>{vm.persistent ? '예' : '아니오'}</TableCell>
                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                      <Tooltip title="VM 정보">
                        <IconButton size="small" onClick={() => navigate(`/vms/${vm.name}`)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {vm.state === 1 && (
                        <Tooltip title="터미널 접속">
                          <IconButton size="small" color="primary" onClick={() => navigate(`/terminal/${vm.name}`)}>
                            <TerminalIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {vm.state !== 1 && (
                        <Tooltip title="VM 시작">
                          <IconButton size="small" color="success" onClick={() => handleStartVM(vm.name)}>
                            <PlayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {vm.state === 1 && (
                        <Tooltip title="VM 종료 (ACPI)">
                          <IconButton size="small" color="warning" onClick={() => handleShutdownVM(vm.name)}>
                            <StopIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {vm.state === 1 && (
                        <Tooltip title="강제 종료">
                          <IconButton size="small" color="error" onClick={() => handleForceStopClick(vm)}>
                            <PowerOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="VM 복제">
                        <IconButton size="small" color="primary" onClick={() => handleOpenCloneDialog(vm)}>
                          <CloneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── 강제 종료 확인 ── */}
      <Dialog open={forceStopDialogOpen} onClose={() => setForceStopDialogOpen(false)}>
        <DialogTitle>VM 강제 종료 확인</DialogTitle>
        <DialogContent>
          <Typography><strong>{vmToForceStop?.name}</strong> VM을 강제 종료하시겠습니까?</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            강제 종료는 즉시 전원을 차단합니다. 저장되지 않은 데이터가 손실될 수 있습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceStopDialogOpen(false)}>취소</Button>
          <Button onClick={handleForceStopConfirm} color="error" variant="contained" startIcon={<PowerOffIcon />}>
            강제 종료
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 복제 다이얼로그 ── */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>가상머신 복제 — {sourceVM?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="new_name" label="새 VM 이름" fullWidth
              value={cloneConfig.new_name}
              onChange={e => setCloneConfig(p => ({ ...p, new_name: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>네트워크 프로필</InputLabel>
              <Select
                name="network_profile" value={cloneConfig.network_profile} label="네트워크 프로필"
                onChange={e => setCloneConfig(p => ({ ...p, network_profile: e.target.value }))}
              >
                <MenuItem value=""><em>원본과 동일</em></MenuItem>
                {networkProfiles.map(p => (
                  <MenuItem key={p.name} value={p.name}>{p.name} ({p.network_type})</MenuItem>
                ))}
              </Select>
            </FormControl>
            {[
              { key: 'include_gpu',          label: 'GPU 설정 포함 (가능한 경우)' },
              { key: 'start_after_clone',    label: '복제 후 VM 시작' },
              { key: 'shutdown_if_running',  label: '원본 VM이 실행 중이면 자동 종료 후 복제' },
              { key: 'clone_port_forwards',  label: '포트 포워딩 규칙 복제' },
              { key: 'clone_services',       label: '서비스 레지스트리 복제' },
            ].map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={cloneConfig[key]}
                    onChange={e => setCloneConfig(p => ({ ...p, [key]: e.target.checked }))}
                  />
                }
                label={label}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>취소</Button>
          <Button onClick={handleCloneVM} variant="contained" disabled={!cloneConfig.new_name}>복제</Button>
        </DialogActions>
      </Dialog>

      {/* ── 일괄 삭제 확인 ── */}
      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>VM 일괄 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            다음 <strong>{selected.size}개</strong> VM을 삭제합니다:
          </Typography>
          <Box
            sx={{
              maxHeight: 200, overflowY: 'auto', bgcolor: 'action.hover',
              borderRadius: 1, p: 1, mb: 2, fontFamily: 'monospace', fontSize: 13,
            }}
          >
            {[...selected].sort().map(name => <div key={name}>{name}</div>)}
          </Box>
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            이 작업은 되돌릴 수 없습니다. 실행 중인 VM은 강제 종료 후 삭제됩니다.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={removeStorage}
                onChange={e => setRemoveStorage(e.target.checked)}
                color="error"
              />
            }
            label="디스크 이미지도 함께 삭제"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>취소</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained" startIcon={<DeleteIcon />}>
            {selected.size}개 삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VMListPage;
